import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Mail, Send, CheckCircle2, AlertTriangle, Clock, RefreshCw, 
  FileText, Users, Settings, History, TestTube, Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  
  // Alert form
  const [alertType, setAlertType] = useState('info');
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  
  const fetchData = async () => {
    try {
      const [configRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/email/config`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/email/logs?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (configRes.ok) setConfig(await configRes.json());
      if (logsRes.ok) {
        const logsData = await logsRes.json();
        // Handle both array and object with logs key
        setLogs(Array.isArray(logsData) ? logsData : (logsData.logs || []));
      }
    } catch (error) {
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [token]);
  
  const sendTestEmail = async () => {
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/email/test`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to send test email', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error sending test email');
    } finally {
      setSending(false);
    }
  };
  
  const sendAlert = async () => {
    if (!alertTitle || !alertMessage) {
      toast.error('Please fill in title and message');
      return;
    }
    
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/email/send-alert`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          alert_type: alertType,
          title: alertTitle,
          message: alertMessage
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setAlertTitle('');
        setAlertMessage('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to send alert', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error sending alert');
    } finally {
      setSending(false);
    }
  };
  
  const sendComplianceReport = async () => {
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/email/send-compliance-report`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ report_type: 'compliance' })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to send report', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error sending report');
    } finally {
      setSending(false);
    }
  };
  
  const sendBurnoutAlert = async () => {
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/email/send-burnout-alert`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ report_type: 'burnout' })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to send alert', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error sending alert');
    } finally {
      setSending(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="email-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
            Email Notifications
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Configure and send email notifications to stakeholders
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      {/* Status Card */}
      <Card className={config?.configured ? "border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/10" : "border-amber-200 bg-amber-50/50"}>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-full ${config?.configured ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-amber-100'}`}>
              <Mail className={`h-6 w-6 ${config?.configured ? 'text-emerald-600' : 'text-amber-600'}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">Email Service Status</h3>
                <Badge variant={config?.configured ? "default" : "secondary"} className={config?.configured ? "bg-emerald-500" : ""}>
                  {config?.configured ? 'Connected' : 'Not Configured'}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {config?.configured 
                  ? `Sending from ${config.smtp_user} via ${config.smtp_host}`
                  : 'Email service needs configuration'}
              </p>
            </div>
            {config?.configured && (
              <Button onClick={sendTestEmail} disabled={sending} variant="outline" size="sm">
                <TestTube className="h-4 w-4 mr-2" />
                Send Test
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Recipients */}
      {config?.default_recipients?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-teal-500" />
              Default Recipients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {config.default_recipients.map((email, idx) => (
                <Badge key={idx} variant="secondary" className="text-sm">
                  {email}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      <Tabs defaultValue="quick-send" className="space-y-4">
        <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-3 h-auto">
          <TabsTrigger value="quick-send" className="gap-2">
            <Send className="h-4 w-4" /> Quick Send
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-2">
            <FileText className="h-4 w-4" /> Reports
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" /> History
          </TabsTrigger>
        </TabsList>
        
        {/* Quick Send Tab */}
        <TabsContent value="quick-send">
          <Card>
            <CardHeader>
              <CardTitle>Send Custom Alert</CardTitle>
              <CardDescription>Send a custom alert notification to all recipients</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Alert Type</Label>
                  <Select value={alertType} onValueChange={setAlertType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="info">Information</SelectItem>
                      <SelectItem value="success">Success</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input 
                    placeholder="Alert title"
                    value={alertTitle}
                    onChange={(e) => setAlertTitle(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea 
                  placeholder="Enter your alert message..."
                  value={alertMessage}
                  onChange={(e) => setAlertMessage(e.target.value)}
                  rows={4}
                />
              </div>
              <Button onClick={sendAlert} disabled={sending || !config?.configured}>
                <Send className="h-4 w-4 mr-2" />
                {sending ? 'Sending...' : 'Send Alert'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Reports Tab */}
        <TabsContent value="reports">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  Compliance Report
                </CardTitle>
                <CardDescription>Send the latest compliance status to stakeholders</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={sendComplianceReport} disabled={sending || !config?.configured} className="w-full">
                  <Send className="h-4 w-4 mr-2" />
                  Send Compliance Report
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Burnout Risk Alert
                </CardTitle>
                <CardDescription>Notify managers about staff at burnout risk</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={sendBurnoutAlert} disabled={sending || !config?.configured} className="w-full" variant="outline">
                  <Bell className="h-4 w-4 mr-2" />
                  Send Burnout Alert
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Email History</CardTitle>
              <CardDescription>Recent emails sent from the system</CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No emails sent yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Recipients</TableHead>
                      <TableHead>Sent At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Badge variant="outline">{log.type}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">{log.subject}</TableCell>
                        <TableCell>{log.recipients?.length || 0} recipients</TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(log.sent_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
